import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CRMApiAdapter } from '../adapters/CRMApiAdapter';
import { Filters } from '../models/Filters';
import { Service } from '../services/Service';

export interface UseCRMFilters extends Filters {}

export const useCRM = (filters: UseCRMFilters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CRMApiAdapter(httpClient);
  const service = new Service(apiAdapter);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useInfiniteQuery({
    queryKey: ['crm-orders', filters],
    queryFn: ({ pageParam = 1 }) => {
      return service.getPaginated({
        ...filters,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage) => lastPage?.nextPage ?? undefined,
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000, // 5 minutos
    placeholderData: (previousData) => previousData,
  });

  const orders = data?.pages.flatMap((page) => page?.orders ?? []) ?? [];

  // Obtener el total del API (siempre sera el mismo en todas las paginas)
  const totalFromApi = data?.pages?.[0]?.total ?? 0;

  return {
    orders,
    isLoading: isLoading || isPending,
    isFetching,
    error,
    refetch,
    // Nuevas props para scroll infinito
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    // Stats útiles
    totalPages: data?.pages.length ?? 0,
    totalItems: orders.length,
    total: totalFromApi, // Total real del API (con filtros)
  };
};
